import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { AnimatePresence, motion } from "framer-motion";
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type ReactNode,
} from "react";

import { cn } from "./utils";

export interface BasicTooltipProps {
  delayDuration?: number;
  content: ReactNode;
  children: ReactNode;
  variant?: "black" | "blue" | "white" | "red";
  side?: "top" | "bottom" | "left" | "right";
  usePortal?: boolean;
  shouldShow?: boolean;
  className?: string;
  contentClassName?: string;
  offset?: number;
  disabled?: boolean;
  ignoreOverflow?: boolean;
}

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = ({ ...props }: ComponentProps<typeof TooltipPrimitive.Root>) => (
  <TooltipPrimitive.Root delayDuration={0} {...props} />
);

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    variant?: "black" | "blue" | "white" | "red";
    contentClassName?: string;
  }
>(({ className, sideOffset = 4, variant = "black", contentClassName, ...props }, ref) => (
  <AnimatePresence>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      align="center"
      side={props.side}
      alignOffset={0}
      avoidCollisions
      collisionPadding={8}
      {...props}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 2 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 2 }}
        transition={{
          type: "spring",
          damping: 20,
          stiffness: 350,
          duration: 0.2,
        }}
      >
        <div
          className={cn(
            "z-[9999] max-h-[240px] max-w-[360px] overflow-y-auto overflow-x-hidden break-words rounded-md px-3 py-1.5 text-xs shadow-sm",
            variant === "black" && "bg-black text-white",
            variant === "blue" && "bg-blue-500 text-white",
            variant === "white" && "border border-gray-200 bg-white text-gray-900",
            variant === "red" && "border border-red-200 bg-red-50 text-red-700",
            contentClassName,
            className,
          )}
        >
          <div className="overflow-wrap-anywhere whitespace-normal">{props.children}</div>
          <TooltipPrimitive.Arrow
            className={cn(
              variant === "black" && "fill-black",
              variant === "blue" && "fill-blue-500",
              variant === "white" && "fill-white",
              variant === "red" && "fill-red-200",
            )}
          />
        </div>
      </motion.div>
    </TooltipPrimitive.Content>
  </AnimatePresence>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const BasicTooltip = forwardRef<HTMLDivElement, BasicTooltipProps>(
  (
    {
      delayDuration,
      content,
      children,
      variant = "black",
      side = "top",
      shouldShow = true,
      usePortal = true,
      offset = 0,
      disabled = false,
      contentClassName,
    },
    ref,
  ) => (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        {usePortal ? (
          <TooltipPrimitive.Portal>
            <TooltipContent
              ref={ref}
              side={side}
              variant={variant}
              contentClassName={contentClassName}
              onClick={e => e.stopPropagation()}
              sideOffset={offset}
              hidden={!shouldShow || disabled}
              style={{ zIndex: 9999 }}
            >
              {content}
            </TooltipContent>
          </TooltipPrimitive.Portal>
        ) : (
          <TooltipContent
            ref={ref}
            side={side}
            sideOffset={offset}
            variant={variant}
            contentClassName={contentClassName}
            hidden={!shouldShow || disabled}
            style={{ zIndex: 9999 }}
            onClick={e => e.stopPropagation()}
          >
            {content}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  ),
);

BasicTooltip.displayName = "BasicTooltip";

export { BasicTooltip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
